import React, { useState } from 'react';
import { Image, Input } from 'antd';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { ISubQuestion } from 'modules/national-certificate';
import { useCheckNationalTest } from 'modules/national-certificate-test';

import { MathJaxContext } from 'components';

interface IProps {
  test?: ISubQuestion[];
}

export const OptionsWithInput: React.FC<IProps> = ({ test }) => {
  const t = useTranslations();
  const { mutate } = useCheckNationalTest();
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null | undefined>(null);

  const handleOptionSelect = (optionId?: number | null) => () => {
    if (!optionId) {
      setSelectedOption(optionId);
    }
  };

  const handleInputChange = (optionId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setAnswers(prev => ({ ...prev, [optionId]: value }));

    if (debounceTimer) clearTimeout(debounceTimer);

    const timer = setTimeout(() => {
      mutate({
        testId: optionId,
        answer: value
      });
    }, 3000);

    setDebounceTimer(timer);
  };

  return (
    <div
      className={classNames('grid grid-cols-1 gap-1', (test?.length || 0) > 1 && 'md:grid-cols-2')}
    >
      {test?.map((option, index) => {
        const isSelected = selectedOption === option.id;

        return (
          <div
            key={option.id}
            className={classNames('rounded-xl border-2', {
              'border-transparent': !isSelected,
              'border-green-500': isSelected
            })}
          >
            <div
              className={classNames(
                'flex h-full cursor-pointer flex-col  justify-between rounded-xl border p-2.5 transition-all',
                {
                  'border-gray-200': !isSelected,
                  'border-transparent': isSelected
                }
              )}
              onClick={handleOptionSelect(option.id)}
            >
              <div className="mb-1 flex items-center gap-3">
                <span className="font-semibold text-gray-600">{String.fromCharCode(97 + index)})</span>
                <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
                  <p className="inline-block whitespace-pre-line text-xs text-gray-800">
                    <MathJaxContext expression={option.question_name} />
                  </p>
                  {option?.image && (
                    <div className="mt-2 flex justify-center">
                      <Image src={option.image} alt={option?.name || 'Test option'} loading="lazy" title="Test image" />
                    </div>
                  )}
                </div>
              </div>
              <Input
                className="!h-8.5 !rounded-lg !bg-gray-100 !text-sm hover:!border-green-500 focus:!border-green-500"
                placeholder={t('javobingizni_yozing')}
                value={option?.answer || answers[option.id] || ''}
                onChange={handleInputChange(option.id)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
